import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShoppingBag, ArrowUpRight } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { bundlePricing, kitFlavorOptions, kitSizeOptions } from "../../utils/bundlePricing";

const BundleCard = ({ bundle, productsById, compact = false, hideLink = false, linkProducts = false }) => {
  const cart = useCart();
  // The admin fixes the kit's price; the customer may pick a gusto and/or
  // formato among the ones the admin allowed that are still in stock — the
  // kit total never moves no matter which combo is picked (fixed-price kit).
  const [flavorChoices, setFlavorChoices] = useState({});
  const [sizeChoices, setSizeChoices] = useState({});

  const { lines, originalTotal, discountedTotal, savings } = bundlePricing(bundle, productsById, flavorChoices, sizeChoices);
  if (lines.some((l) => !l.product)) return null; // a referenced product is no longer active

  const pickFlavor = (productId, value) =>
    setFlavorChoices((c) => ({ ...c, [productId]: value }));
  const pickSize = (productId, value) =>
    setSizeChoices((c) => ({ ...c, [productId]: value }));

  const handleAdd = () => {
    cart.addBundle(bundle, productsById, flavorChoices, sizeChoices);
  };

  const detailTo = !hideLink && bundle.slug ? `/offerta/${bundle.slug}` : null;

  return (
    <div
      data-testid={`bundle-card-${bundle.id}`}
      className={`bg-[var(--lv-surface,#0A1121)] border border-[#C0C0C0]/20 rounded-md overflow-hidden ${compact ? "" : "hover:border-[#C0C0C0]/50 transition-colors"}`}
    >
      {bundle.image && (
        detailTo ? (
          <Link to={detailTo} data-testid={`bundle-image-link-${bundle.id}`} className="block w-full h-40 sm:h-48 bg-white overflow-hidden">
            <img src={bundle.image} alt={bundle.name} data-testid={`bundle-image-${bundle.id}`}
                 className="w-full h-full object-cover" />
          </Link>
        ) : (
          <div className="w-full h-40 sm:h-48 bg-white overflow-hidden">
            <img src={bundle.image} alt={bundle.name} data-testid={`bundle-image-${bundle.id}`}
                 className="w-full h-full object-cover" />
          </div>
        )
      )}
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-3.5 h-3.5 text-[#C0C0C0]" />
          <span className="text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0]">
            Kit{bundle.goal ? ` · ${bundle.goal}` : ""}
          </span>
        </div>
        <h3 className="font-[Outfit] text-white text-xl tracking-tight mb-1" data-testid={`bundle-name-${bundle.id}`}>
          {detailTo ? (
            <Link to={detailTo} data-testid={`bundle-name-link-${bundle.id}`} className="hover:text-[#C0C0C0] transition-colors">{bundle.name}</Link>
          ) : bundle.name}
        </h3>
        {bundle.description && <p className="text-[13px] text-[#94A3B8] mb-4">{bundle.description}</p>}

        <div className="space-y-3 mb-4">
          {lines.map(({ product, item, flavor, size }) => {
            const flavorOptions = kitFlavorOptions(item, product, size);
            const sizeOptions = kitSizeOptions(item, product, flavor);
            const productTo = linkProducts && product.slug ? `/prodotto/${product.slug}` : null;
            const ImgWrap = productTo ? Link : "div";
            const imgProps = productTo ? { to: productTo, "data-testid": `bundle-item-image-link-${bundle.id}-${item.product_id}` } : {};
            return (
              <div key={item.product_id} className="flex items-center gap-3" data-testid={`bundle-item-${bundle.id}-${item.product_id}`}>
                <ImgWrap {...imgProps} className="w-12 h-12 rounded-sm bg-white flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {product.image && (
                    <img src={product.image} alt={product.name} className="max-w-full max-h-full object-contain p-1" />
                  )}
                </ImgWrap>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-white truncate" data-testid={`bundle-item-name-${bundle.id}-${item.product_id}`}>
                    {productTo ? (
                      <Link to={productTo} data-testid={`bundle-item-name-link-${bundle.id}-${item.product_id}`} className="hover:text-[#C0C0C0] transition-colors">{product.name}</Link>
                    ) : product.name}
                    {(item.quantity || 1) > 1 && (
                      <span className="ml-1.5 text-[#C0C0C0] font-medium" data-testid={`bundle-item-qty-${bundle.id}-${item.product_id}`}>× {item.quantity}</span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {flavorOptions.length > 1 ? (
                      <select
                        value={flavor}
                        onChange={(e) => pickFlavor(item.product_id, e.target.value)}
                        data-testid={`bundle-flavor-${bundle.id}-${item.product_id}`}
                        className="bg-[#060A14] border border-white/10 rounded-sm text-[10px] text-[#94A3B8] px-1.5 py-0.5 focus:outline-none focus:border-[#C0C0C0]"
                      >
                        {flavorOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    ) : (
                      flavor && <span className="text-[10px] text-[#94A3B8]">{flavor}</span>
                    )}
                    {sizeOptions.length > 1 ? (
                      <select
                        value={size}
                        onChange={(e) => pickSize(item.product_id, e.target.value)}
                        data-testid={`bundle-size-${bundle.id}-${item.product_id}`}
                        className="bg-[#060A14] border border-white/10 rounded-sm text-[10px] text-[#94A3B8] px-1.5 py-0.5 focus:outline-none focus:border-[#C0C0C0]"
                      >
                        {sizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      size && (
                        <span
                          className="text-[10px] text-[#94A3B8]"
                          data-testid={`bundle-size-readonly-${bundle.id}-${item.product_id}`}
                        >
                          {size}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-end justify-between pt-4 border-t border-white/5 gap-3">
          <div>
            <p className="text-[10px] text-[#94A3B8] tracking-[0.16em] uppercase">Prezzo kit</p>
            <p className="flex items-baseline gap-2">
              {originalTotal > discountedTotal && (
                <span className="text-[#94A3B8] line-through text-sm" data-testid={`bundle-original-${bundle.id}`}>
                  €{originalTotal.toFixed(2)}
                </span>
              )}
              <span className="text-white text-2xl font-[Outfit]" data-testid={`bundle-price-${bundle.id}`}>
                €{discountedTotal.toFixed(2)}
              </span>
            </p>
            {savings > 0 && (
              <p className="text-[11px] text-emerald-300" data-testid={`bundle-savings-${bundle.id}`}>
                Risparmi €{savings.toFixed(2)}
              </p>
            )}
          </div>
          {detailTo && (
            <Link
              to={detailTo}
              data-testid={`bundle-details-${bundle.id}`}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#94A3B8] hover:text-white transition-colors whitespace-nowrap"
            >
              Dettagli <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          )}
          <button
            onClick={handleAdd}
            data-testid={`bundle-add-${bundle.id}`}
            className="inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-4 py-2.5 rounded-sm text-[10px] tracking-[0.22em] uppercase font-medium hover:bg-white transition-colors whitespace-nowrap"
          >
            <ShoppingBag className="w-3.5 h-3.5" /> Aggiungi il kit
          </button>
        </div>
      </div>
    </div>
  );
};

export default BundleCard;

